import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ImageListModal, ImageModal } from '@/shared/components/ui/Images';
import {
  EstadosImagenChoiceEnum,
  calcOTImages,
  solicitudUpdFotosSchema,
} from '@/shared';
import {
  OkFotosEntradasUpddActivacion,
  useUpdateOrdenTrabajo,
} from '@/shared/actions/erp';
import { UpdFotosEntradaRequestData } from '@/shared/actions/erp/tecnico';
import { TabTextLabel } from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { OrdenTrabajo } from '@/shared/interfaces';
import { useAuthStore } from '@/store/auth';
import { useUiStore } from '@/store/ui';
import ConfirmUpdFotosAlertActivacionFotosModal from './ConfirmUpdFotosAlertActivacionFotosModal';

export type FotosEntradaUpdatedReviewModalProps = {
  ordenTrabajo: OrdenTrabajo;
  open: boolean;
  onClose: () => void;
};

type FormData = {
  comentario_solicita_upd_fotos: string;
  fecha_solicita_upd_fotos: string;
  usuario_solicita_upd_fotos: number;
};

const FotosEntradaUpdatedReviewModal: React.FC<
  FotosEntradaUpdatedReviewModalProps
> = ({ ordenTrabajo, onClose, open }) => {
  ///* local state ------------
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
  const [imagesArr, setImagesArr] = useState<ImageModal[]>([]);

  ///* global state ------------
  const user = useAuthStore(s => s.user);
  const setIsGlobalLoading = useUiStore(s => s.setIsGlobalLoading);

  ///* form ------------
  const form = useForm<FormData>({
    resolver: yupResolver(solicitudUpdFotosSchema) as any,
    defaultValues: {
      fecha_solicita_upd_fotos: dayjs().format(),
      usuario_solicita_upd_fotos: user?.user_id!,
    },
  });

  ///* mutations ------------
  const updOtOkUpdatedFotos =
    useUpdateOrdenTrabajo<OkFotosEntradasUpddActivacion>({
      enableRedirect: false,
    });
  const updOtFotosEntrada = useUpdateOrdenTrabajo<UpdFotosEntradaRequestData>({
    enableRedirect: false,
  });

  ///* handlers ------------
  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async () => {
    setIsGlobalLoading(true);
    handleClose();

    updOtOkUpdatedFotos.mutate({
      id: ordenTrabajo?.id_orden_trabajo!,
      data: {
        estado_imagen_actualizacion: EstadosImagenChoiceEnum.UPD_APROBADA,
        estado_imagen_entrada: EstadosImagenChoiceEnum.CARGADO,
        estado_imagen_salida: EstadosImagenChoiceEnum.CARGADO,
      },
    });

    handleClose();
  };

  const handleRequestUpd = async (data: FormData) => {
    updOtFotosEntrada.mutateAsync({
      id: ordenTrabajo?.id_orden_trabajo!,
      data: {
        estado_imagen_entrada: EstadosImagenChoiceEnum.UPD_SOLICITADA,
        estado_imagen_actualizacion: EstadosImagenChoiceEnum.UPD_SOLICITADA,
        observaciones: {
          ...ordenTrabajo?.observaciones,
          observacion_foto_entrada_upd_sol: data.comentario_solicita_upd_fotos,
        },
      },
    });

    onClose();
  };

  ///* effects ------------
  useEffect(() => {
    if (!open) return;

    // antes era async
    const photesEntrada = calcOTImages(ordenTrabajo, 'urls_fotos_entrada');
    setImagesArr(photesEntrada);
  }, [open]);

  return (
    <>
      <ScrollableDialogProps
        open={open}
        title="Fotos de Entrada Actualizadas"
        minWidth="54%"
        minHeight="30vh"
        contentNode={
          <>
            <Grid item xs={12} container spacing={3}>
              <>
                {/* ----- Image Modal ----- */}
                <Grid item xs={12}>
                  <TabTextLabel text="Fotos de Entrada" pt="0" pl="0" pb="0" />
                </Grid>
                <ImageListModal images={imagesArr || []} />
              </>
            </Grid>
          </>
        }
        onClose={handleClose}
        showCustomButtons
        customButtons={
          <>
            <Grid
              item
              container
              xs={12}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button
                onClick={() => {
                  handleClose();
                }}
                variant="text"
                color="inherit"
              >
                Cerrar
              </Button>
              <span className="spacer" />

              {/* ---------- REJECT ---------- */}
              <Button
                onClick={() => {
                  setIsOpenConfirmDialog(true);
                }}
                color="error"
              >
                Solicitar Actualización de Fotos
              </Button>

              {/* ---------- CONFIRM ---------- */}
              <Button
                onClick={() => {
                  handleConfirm();
                }}
                variant="outlined"
                color="primary"
              >
                Validar Actualización
              </Button>
            </Grid>
          </>
        }
      />

      {/* ========= confirm dialog ========= */}
      <ConfirmUpdFotosAlertActivacionFotosModal
        form={form}
        handleConfirm={handleRequestUpd}
        isOpenConfirmDialog={isOpenConfirmDialog}
        setIsOpenConfirmDialog={setIsOpenConfirmDialog}
      />
    </>
  );
};

export default FotosEntradaUpdatedReviewModal;
